import React from 'react'
import { useTranslation } from 'react-i18next'
import { Fixed } from '../../parts/bases/Fixed'
import { HStack } from '../../parts/bases/HStack'
import { Text } from '../../parts/bases/Text'
import { VStack } from '../../parts/bases/VStack'
import { PinkNeonButton } from '../../parts/buttons/PinkNeonButton'
import Image from 'next/image'
import { Center } from '../../parts/bases/Center'
import { Spinner } from '../../parts/commons/Spinner'
import config from '@/tailwind.config'
import Teras from '@/public/images/teras.png'
import AmInstallArrow from '@/public/images/amInstallArrow.png'
import { Absolute } from '../../parts/bases/Absolute'
import JunkStarOn from '@/public/images/junkStarOn.png'
import JunkStarOff from '@/public/images/junkStarOff.png'
import Arrow from '@/public/images/amInstallArrow.png'
import { Space } from '../../parts/bases/Space'

export type Props = {
  status: 'Prepare' | 'Loading' | 'Completed'
  onClickConvert: () => void
  onClickClose: () => void
  terasBalance: number
  fee: number
  junk: {
    imageUrl: string
    name: string
    amount: number
    rarity: number
    junksPerPart: number
    convertedAp: { imageUrl: string; name: string }
  }
  numOfConvertItems: number
  onChangeNumOfConvertItems: (n: number) => void
  convertable: boolean
}

export const Layout = (props: Props) => {
  const { t } = useTranslation()
  return (
    <Fixed className="top-0 left-0 right-0 bottom-0 z-[100] h-full w-full items-center justify-center bg-dialogBg">
      <div className="flex h-full w-full items-center justify-center" onClick={props.onClickClose}>
        <VStack
          className="items-center justify-center rounded-xl border-2 border-neon-blue bg-black p-2"
          onClick={(event) => event.stopPropagation()}
        >
          {props.status === 'Prepare' ? (
            <VStack className="w-full items-center">
              <Text className="m-6 text-2xl font-bold text-white">{t('JunkConvertDialog.title')}</Text>
              <HStack className="items-start">
                <Center className="relative flex-col">
                  <div
                    className={`h-[220px] w-[150px] overflow-hidden rounded-xl border-2 border-[#5F5B7A] bg-black bg-opacity-70`}
                  >
                    <VStack className="h-full w-full bg-[url('/images/gameCenterCellTextBg.png')] bg-repeat">
                      <div
                        className="relative aspect-square w-full bg-black bg-cover bg-center"
                        style={{ backgroundImage: `url('${props.junk.imageUrl}')` }}
                      >
                        <Absolute className="left-0 bottom-0 w-full flex-row items-end p-2">
                          <HStack>
                            {[...Array(5)].map((_, index) => (
                              <Center key={index} className="mr-0.5">
                                <Image
                                  src={index < props.junk.rarity ? JunkStarOn : JunkStarOff}
                                  width={12}
                                  height={12}
                                  alt=""
                                />
                              </Center>
                            ))}
                          </HStack>
                          <Space />
                        </Absolute>
                      </div>
                      <VStack className="w-full flex-1 p-2">
                        <Text className="flex-1 text-left text-xs font-bold text-white">
                          {props.junk.name}
                        </Text>
                        <HStack className="w-full items-baseline justify-end">
                          <Text className="text-xs font-bold text-white">In Akiverse</Text>
                          <Text className="mx-2 text-xs font-bold text-white">x</Text>
                          <Text className="text-xs font-bold text-neon-blue">{props.junk.amount || '-'}</Text>
                        </HStack>
                      </VStack>
                    </VStack>
                  </div>
                  <Center className="h-[50px]">
                    <Text className="mt-4 text-sm font-bold text-white">
                      Consume JunkAP
                      <Text className="ml-2 text-akiverse-blue">{props.junk.junksPerPart}</Text>
                    </Text>
                  </Center>
                </Center>
                <Center className="mx-10 mt-24">
                  <Image src={Arrow} width={24} height={30} alt="" />
                </Center>
                <Center className="relative flex-col">
                  <div
                    className={`h-[220px] w-[150px] overflow-hidden rounded-xl border-2 border-[#805BE7] bg-black bg-gradient-to-b from-[rgba(0,0,0,0)] via-[rgba(0,0,0,0)] to-[#805BE760]`}
                  >
                    <VStack className="h-full w-full bg-[url('/images/gameCenterCellTextBg.png')] bg-repeat">
                      <div
                        className="aspect-square w-full bg-black bg-cover bg-center"
                        style={{ backgroundImage: `url('${props.junk.convertedAp.imageUrl}')` }}
                      />
                      <VStack className="w-full p-2">
                        <Text className="flex-1 text-left text-xs font-bold text-white">
                          {props.junk.convertedAp.name}
                        </Text>
                      </VStack>
                    </VStack>
                  </div>
                  <Center className="h-[50px]">
                    <Text className="mt-4 text-sm font-bold text-white">
                      Receive AP
                      {Math.floor(props.junk.amount / props.junk.junksPerPart) >= 2 ? (
                        <select
                          name="automaticRenewal"
                          id="automaticRenewal-select"
                          value={props.numOfConvertItems}
                          onChange={(e) => props.onChangeNumOfConvertItems(Number(e.target.value))}
                          className="ml-4 h-7 rounded-md border border-neutral-500 bg-black px-3 text-sm text-neon-blue"
                        >
                          {[...Array(Math.floor(props.junk.amount / props.junk.junksPerPart))].map(
                            (_, index) => (
                              <option key={index} value={index + 1}>
                                {index + 1}
                              </option>
                            ),
                          )}
                        </select>
                      ) : (
                        <Text className="ml-2 text-akiverse-blue">1</Text>
                      )}
                    </Text>
                  </Center>
                </Center>
              </HStack>
              <Text className="m-8 text-center text-sm font-bold text-white">
                {t('JunkConvertDialog.description1')}
                <Image src={Teras} width={12} height={12} alt="" className="ml-1" />
                <Text className="mx-2 ml-1 text-akiverse-akir">{props.fee.toLocaleString()}</Text>
                {'\n'}
                {t('JunkConvertDialog.description2')}
              </Text>
              <HStack className="mb-8 w-[300px] items-center rounded-sm bg-[#00FBEC] bg-opacity-10 py-2 px-4">
                <Center className="mr-5 h-[18px] w-[18px]">
                  <Image src={Teras} width={18} height={18} alt="" />
                </Center>
                <Text className="flex-1 text-center text-base font-bold text-neon-blue">
                  {Number(props.terasBalance).toLocaleString()}
                </Text>
                <Center className="h-[8px] w-[10px]">
                  <Image src={AmInstallArrow} width={8} height={10} alt="" />
                </Center>
                <Text className="flex-1 text-center text-base font-bold text-[#1573FF]">
                  {(props.terasBalance - props.fee).toLocaleString()}
                </Text>
              </HStack>
              <HStack className="mb-8">
                <PinkNeonButton onClick={props.onClickClose} type="Sub">
                  {t('JunkConvertDialog.cancelButton')}
                </PinkNeonButton>
                <VStack className="w-4" />
                <PinkNeonButton onClick={props.onClickConvert} disabled={!props.convertable}>
                  {t('JunkConvertDialog.convertButton')}
                </PinkNeonButton>
              </HStack>
            </VStack>
          ) : null}
          {props.status === 'Loading' ? (
            <Center className="h-[50px] w-[400px]">
              <Spinner size={30} visible color={config.theme.extend.colors.neon.blue} />
            </Center>
          ) : null}
          {props.status === 'Completed' ? (
            <VStack className="w-[500px] items-center">
              <Text className="m-6 text-center text-base font-bold text-white">
                {t('JunkConvertDialog.completedMessage', {
                  numOfAps: props.numOfConvertItems,
                  amount: props.numOfConvertItems * props.junk.junksPerPart,
                  junkName: props.junk.name,
                  apName: props.junk.convertedAp.name,
                })}
              </Text>
            </VStack>
          ) : null}
        </VStack>
      </div>
    </Fixed>
  )
}
